import { getChartDataNew } from "@/controllers/chart-data";
import { getIndexValue, getStockPeers } from "@/controllers/stock-peers";
import type { PeerRow } from "@/models/stock-peers";
import { classNames } from "@/lib/utils";
import { Stock } from "@/models/stock";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import UpgradePlan from "../upgrade-plan/UpgradePlan";
import TimeSeriesChart, { type TimeSeries, type TimeSeriesAxis } from "@/components/ortex-charts/TimeSeriesChart";
import styles from "./CorrelatedPeersChart.module.scss";

const SP500 = "$S_2668699";

const closes = (rows: number[][] | undefined): [number, number][] => (rows ?? []).map((r) => [r[0], r.length > 2 ? r[4] : r[1]] as [number, number]);

type PeerLine = { id: number; name: string; data: [number, number][] };

/** The closest peer's price line, keyed by trading item id. */
const getPeerLine = async (peer: PeerRow): Promise<PeerLine | null> => {
    const id = Number(peer.peerTradingitemId);
    if (!id) return null;
    return { id, name: String(peer.ticker ?? id), data: closes(await getChartDataNew(id, "ohlcv")) };
};

/** Standalone use (custom views): one request, page size 1, for the stock's closest peer. */
const fetchTopPeer = async (tradingitemId: number): Promise<PeerRow | null> => {
    const res = await getStockPeers({ page_size: 1, page: 1, filter_options: {}, data: { tradingitem_id: tradingitemId } });
    return res.rows?.[0] ?? null;
};

interface CorrelatedPeersChartProps {
    stock: Stock;
    /** Peers the user added from the table, on the same percent scale. */
    extraSeries?: TimeSeries[];
    /**
     * Draw the stock's closest peer (default). Standalone (custom views) the chart fetches it itself,
     * one request with page size 1. Under the peers grid the page passes `false` and puts the closest
     * peer on the chart through `extraSeries`, so the grid's Show on Chart button knows it is there.
     */
    drawTopPeer?: boolean;
}

const PERCENT_AXES: TimeSeriesAxis[] = [{ id: "right", mode: "percent" }];

/** The stock, the S&P 500 and its closest peer as percent change from the start of the view, volume below. */
const CorrelatedPeersChart = ({ stock, extraSeries, drawTopPeer = true }: CorrelatedPeersChartProps) => {
    const [chartObfuscated, setChartObfuscated] = useState(false);
    const [chartShortened, setChartShortened] = useState(false);
    const [index, setIndex] = useState<[number, number][]>([]);
    const [bars, setBars] = useState<number[][]>([]);
    const [peer, setPeer] = useState<PeerLine | null>(null);

    useEffect(() => {
        let alive = true;
        setIndex([]);
        setBars([]);
        getIndexValue(SP500).then((data) => {
            if (!alive) return;
            setChartObfuscated(data.obfuscated);
            setChartShortened(data.ts_shortened);
            setIndex(data.ts ?? []);
        });
        getChartDataNew(stock.tradingitem_id, "ohlcv", dayjs().subtract(5, "years").format("YYYY-MM-DD")).then((data) => alive && setBars(data ?? []));
        return () => {
            alive = false;
        };
    }, [stock.tradingitem_id]);

    // The closest peer's line, standalone only (see `drawTopPeer`).
    useEffect(() => {
        let alive = true;
        setPeer(null);
        if (!drawTopPeer) return;
        fetchTopPeer(stock.tradingitem_id)
            .then((row) => (row ? getPeerLine(row) : null))
            .then((line) => alive && setPeer(line))
            .catch((err) => console.error("[CorrelatedPeersChart] Could not load the closest peer:", err));
        return () => {
            alive = false;
        };
    }, [stock.tradingitem_id, drawTopPeer]);

    const series = useMemo<TimeSeries[]>(
        () => [
            { id: `stock-${stock.tradingitem_id}`, title: stock.ticker, type: "line", data: closes(bars), precision: 2 },
            { id: "sp500", title: "S&P 500", type: "spline", data: index, precision: 2 },
            // Prefixed so it can never collide with a line the page passes in through `extraSeries`.
            ...(peer ? [{ id: `top-peer-${peer.id}`, title: peer.name, type: "line" as const, data: peer.data, precision: 2 }] : []),
            ...(extraSeries ?? []),
            { id: "volume", title: "Volume", type: "column", data: bars.map((d) => [d[0], d[5]] as [number, number]), color: "dodgerblue", pane: "volume", format: "compact" },
        ],
        [bars, index, peer, extraSeries, stock.tradingitem_id, stock.ticker]
    );

    return (
        <div className={classNames(styles["chart-container"], chartObfuscated && !chartShortened && "obfuscated")}>
            {chartShortened && <UpgradePlan />}
            <TimeSeriesChart series={series} axes={PERCENT_AXES} paneHeights={{ volume: 0.35 }} initialRange="1y" exportName={`${stock.ticker}-peers`} />
        </div>
    );
};

export default CorrelatedPeersChart;
